import React, { useState } from 'react';
import { gql, useMutation, useNavigate, useParams } from '@common/hooks';
import {
  SubmitButton,
  Icon,
  Title,
  Gap,
  Form,
  Input,
} from '@common/components';
import { Colors } from '@common/tokens';
import { createMessage } from 'works/legacy/lib/message-helper';
import { PasswordValidationFeedback } from '@identity/components';
import { passwordValidator } from '@identity/services';

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetWorksUserPassword($input: ResetWorksUserPasswordInput!) {
    resetWorksUserPassword(input: $input) {
      worksUser {
        id
      }
    }
  }
`;

type Props = {
  title: string;
  passwordName: string;
  successMessage: string;
  submitButtomLabel: string;
};

export function ResetPasswordForm({
  title,
  passwordName,
  successMessage,
  submitButtomLabel,
}: Props) {
  const [form] = Form.useForm();
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');

  const [resetWorksUserPassword] = useMutation(RESET_PASSWORD_MUTATION);

  const onFinish = (values: Record<string, string>) => {
    resetWorksUserPassword({
      variables: {
        input: {
          token: token,
          password: values.password,
          passwordConfirmation: values.passwordConfirmation,
        },
      },
    }).then((res) => {
      if (res['errors']) {
        let msg;
        if (
          res['errors'][0]['extensions'] &&
          res['errors'][0]['extensions']['code'] === 'BAD_USER_INPUT'
        ) {
          msg = res['errors'][0]['message'];
        } else {
          msg = 'There was a problem with your request, try again.';
        }

        createMessage('error', msg);
        navigate('/works/auth/forgot-password');
      } else {
        createMessage('success', successMessage);
        navigate('/works/sign_in');
      }
    });
  };

  return (
    <div className="ResetPasswordForm">
      <Gap vertical size="md">
        <Title size="h1">{title}</Title>

        <Form form={form} onFinish={onFinish} style={{ width: '100%' }}>
          <Form.Item
            name="password"
            rules={[
              {
                required: true,
                message: `Please create your ${passwordName}.`,
              },
              {
                validator: (_, value) => {
                  if (passwordValidator(value)) {
                    return Promise.reject('Password must include:');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              size="large"
              type="password"
              prefix={<Icon icon="unlock" kind="light" />}
              placeholder={`create ${passwordName}`}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="off"
            />
          </Form.Item>
          <PasswordValidationFeedback password={newPassword} />
          <Form.Item
            name="passwordConfirmation"
            rules={[
              {
                required: true,
                message: `Please confirm your ${passwordName}.`,
              },
              {
                validator: (_, value) => {
                  if (value && value.length > 0 && newPassword !== value) {
                    return Promise.reject(
                      'Password does not match what you entered initially.'
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              prefix={<Icon icon="lock" kind="light" />}
              type="password"
              placeholder={`confirm ${passwordName}`}
              size="large"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item>
            <SubmitButton>{submitButtomLabel}</SubmitButton>
          </Form.Item>
        </Form>
      </Gap>

      <style jsx>{`
        .ResetPasswordForm :global(.ant-input-prefix) {
          color: ${Colors.steelDarker};
        }
        .ResetPasswordForm :global(input) {
          padding-left: 0.4rem;
        }

        .ResetPasswordForm :global(button) {
          width: auto !important;
        }

        .ResetPasswordForm :global(.password-validation-feedback) {
          margin-bottom: 16px;
        }
      `}</style>
    </div>
  );
}
